import React, { useContext, useEffect } from 'react';
import { Router } from 'root/router';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { TopBar, LeftBar, GlobalSocket } from './core';
import { navBarStyles } from './core/style';
import { authActions, notificationActions, permissionsActions, systemActions, userPreferencesActions } from 'store';
import { systemService } from 'store/system/system.service';
import { ReportPopup } from 'pages';
import { DrawerContext, globalSessionENums, PermissionList, RolePermission } from 'utils';
import { getToken } from 'utils/crypto';
import { AiAssistantPanel, AiAssistantProvider } from '../aiAssistant';

export const MenuBar = () => {
    const { accessToken, therapySession } = useSelector((state) => ({
        accessToken: state.auth.accessToken,
        therapySession: state.auth.therapySessionEvents?.[globalSessionENums.REPORT_GENERATION],
    }));
    useSelector((state) => state.permissions.myPermissionsList);
    const classes = navBarStyles();
    const dispatch = useDispatch();
    const token = accessToken || getToken();
    const location = useLocation();
    const { block } = useContext(DrawerContext);
    const info = location?.state;
    const isAppointmentPage = /^\/appointment\//.test(location?.pathname) || /^\/client\/[^/]+\/manual-data\/?$/.test(location?.pathname);
    const isTemplatePage = /^\/template\//.test(location?.pathname) ||    /^\/progress-report\//.test(location?.pathname)  || location?.pathname === '/create-form'      ;
    const reportSession = therapySession?.evenType === globalSessionENums.REPORT_GENERATION && therapySession?.data;

    useEffect(() => {
        if (token) {
            dispatch(authActions.getMyProfile());
            dispatch(systemActions.getCompany());
            dispatch(permissionsActions.getMyPermissions());
            dispatch(notificationActions.getUnRenderNotifications());
            // Saved grid filters, every tab in one call. This is the ONLY read of them —
            // no list endpoint returns preferences, so a filter change never costs the
            // request that fetches the filtered rows. See docs/modules/user-preferences.md.
            dispatch(userPreferencesActions.getUserPreferences());
            // Google calendars connected before we captured the time zone were created in UTC (appts shifted by
            // the UTC offset). Fire-and-forget: the API picks a zone once (and resumes an unfinished re-sync),
            // otherwise ignores this.
            systemService.detectGoogleCalendarTimeZoneService(Intl.DateTimeFormat().resolvedOptions().timeZone).catch(() => {});
        }
    }, [dispatch, token]);

    const renderMainContentStyle = () => {
        if (isTemplatePage) {
            return {
                height: '100%',
                minHeight: '100vh',
                width: 'calc(100% - 220px)',
                padding: block ? '0' : `0 0 0 64px`,
            };
        } else if (isAppointmentPage) {
            return {
                height: '100%',
                minHeight: '100vh',
                width: '100%',
                padding: '0',
            };
        } else {
            const paddingTop = info?.viewType === 'calendar' ? '64px' : '78px';
            const paddingLeft = info?.viewType === 'calendar' ? (block ? '0' : '64px') : block ? '24px' : '84px';
            const paddingRight = info?.viewType === 'calendar' ? '0' : '24px';
            const paddingBottom = info?.viewType === 'calendar' ? '0' : '24px';

            return {
                height: '100%',
                minHeight: '100vh',
                width: 'calc(100% - 220px)',
                padding: `${paddingTop} ${paddingRight} ${paddingBottom} ${paddingLeft}`,
            };
        }
    };

    return (
        <AiAssistantProvider>
            <div className={classes.root}>
                <GlobalSocket />

                {!isAppointmentPage && (
                    <>
                        {!isTemplatePage && <TopBar />}
                        <LeftBar />
                    </>
                )}

                <main className={classes.content} style={renderMainContentStyle()}>
                    <Router />
                </main>

                {token && RolePermission([PermissionList.REPORT_MANAGE?.code]) && reportSession && (
                    <ReportPopup reportSession={reportSession} />
                )}

                {token && <AiAssistantPanel />}
            </div>
        </AiAssistantProvider>
    );
};
